import { useDebounce } from "hooks/useDebounce";
import { useQueryParams } from "hooks/useQueryParams";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOmnisearchContext } from "../../providers/OmnisearchContext";

interface UseOmnisearchInputOptions {
  isURLDriven: boolean;
  initialFilters?: { field: string; value: string | boolean }[];
}

/**
 * useOmnisearchInput manages the omnisearch input value.
 *
 * If inside an OmnisearchProvider, it uses the context's state.
 * Otherwise, it manages state locally.
 *
 * If `isURLDriven` is true, the omnisearch state is synced with the `omnisearch` query param.
 * This takes precedence over context for initialization but writes back to context if available.
 *
 * Automatically resets the query param to default (removes `omnisearch`) when set to empty string.
 */
export function useOmnisearchInput({
  isURLDriven,
  initialFilters = [],
}: UseOmnisearchInputOptions) {
  const omnisearchContext = useOmnisearchContext();
  const isContextDriven = !!omnisearchContext;

  /**
   * Intentionally not destructuring because hook will not always be run within a context
   */
  const contextOmnisearch = omnisearchContext?.omnisearch;
  const setContextOmnisearch = omnisearchContext?.setOmnisearch;
  const hasUserSetOmnisearchContext = omnisearchContext?.hasUserSetOmnisearch;

  const initialFilterOmnisearch = useMemo(() => {
    return initialFilters.length
      ? initialFilters.map((f) => `${f.field}:${f.value}`).join(" ")
      : "";
  }, [initialFilters]);

  const { filters: queryParams, setFilters: setQueryParams } = useQueryParams<{
    omnisearch?: string;
  }>();

  /**
   * Determine initial value precedence: URL > Context > Initial Filters > Empty
   */
  const getInitialValue = useCallback(() => {
    if (isURLDriven && queryParams.omnisearch) {
      return queryParams.omnisearch;
    }
    if (isContextDriven && contextOmnisearch) {
      return contextOmnisearch;
    }
    return initialFilterOmnisearch;
  }, [
    isURLDriven,
    queryParams.omnisearch,
    isContextDriven,
    contextOmnisearch,
    initialFilterOmnisearch,
  ]);

  const [hasUserSetOmnisearch, setHasUserSetOmnisearch] = useState(false);
  const [inputValue, setInputValue] = useState<string>(getInitialValue);
  const [debouncedInputValue] = useDebounce(inputValue, 1000);
  const isInitialRender = useRef(true);

  // Effect: Update local input value when URL omnisearch value changes
  useEffect(() => {
    const wasInitialRender = isInitialRender.current;
    isInitialRender.current = false;
    if (isURLDriven && queryParams.omnisearch !== inputValue) {
      // If there are no initial query params, skip the initial render to avoid overriding any initialFilters values.
      if (wasInitialRender && !queryParams.omnisearch) {
        return;
      }
      setInputValue(queryParams.omnisearch ?? "");
    }
  }, [queryParams.omnisearch]);

  // Effect: Update local input value when context omnisearch value changes
  useEffect(() => {
    if (!isURLDriven && isContextDriven && inputValue !== contextOmnisearch) {
      setOmnisearch(contextOmnisearch ?? "");
    }
  }, [contextOmnisearch]);

  useEffect(() => {
    if (
      isContextDriven &&
      hasUserSetOmnisearchContext &&
      !hasUserSetOmnisearch
    ) {
      setHasUserSetOmnisearch(hasUserSetOmnisearchContext);
    }
  }, [hasUserSetOmnisearchContext]);

  // Effect 1: Sync local input changes to Context and/or URL
  useEffect(() => {
    if (
      !hasUserSetOmnisearch &&
      !initialFilterOmnisearch &&
      !queryParams.omnisearch
    ) {
      if (!isContextDriven && !isURLDriven) return;
    }

    const valueToSync = inputValue.trim();

    if (isContextDriven && setContextOmnisearch) {
      setContextOmnisearch(valueToSync);
    }

    // Update URL if driven by it
    if (isURLDriven) {
      setQueryParams((prev) => {
        const currentQueryParam = prev.omnisearch ?? "";
        // Prevent unnecessary updates if query param already matches
        if (valueToSync === currentQueryParam) {
          return; // No change needed - return undefined to signal no state change
        }
        // Create a new params object for the update
        const newParams = { ...prev };
        if (!valueToSync) {
          delete newParams.omnisearch;
        } else {
          newParams.omnisearch = valueToSync;
        }
        return newParams; // Return the new object
      });
    }
  }, [
    inputValue,
    isContextDriven,
    setContextOmnisearch,
    isURLDriven,
    setQueryParams,
    hasUserSetOmnisearch,
    initialFilterOmnisearch,
  ]);

  // Effect 2: Set initial query param if needed (only if URLDriven and no existing param)
  useEffect(() => {
    if (
      isURLDriven &&
      !hasUserSetOmnisearch &&
      !queryParams.omnisearch &&
      initialFilterOmnisearch
    ) {
      setQueryParams((prev) => {
        prev.omnisearch = initialFilterOmnisearch;
      });
    }
  }, [
    isURLDriven,
    hasUserSetOmnisearch,
    queryParams.omnisearch,
    initialFilterOmnisearch,
    setQueryParams,
  ]);

  const setOmnisearch = useCallback(
    (val: string) => {
      if (val !== inputValue) {
        setInputValue(val);
        if (!hasUserSetOmnisearch) {
          setHasUserSetOmnisearch(true);
        }
      }
    },
    [hasUserSetOmnisearch, inputValue]
  );

  const getOmnisearchValue = useCallback(
    (key: string) => {
      const { kvpValues } = breakApartOmnisearch(inputValue);
      const kvp = kvpValues.find(([k]) => k === key);
      return kvp?.[1];
    },
    [inputValue]
  );

  const createUrlFromOmnisearchKeyValues = useCallback(
    (params: { keyValues: [string, string][]; keysToKeep?: string[] }) => {
      const { keyValues, keysToKeep } = params;
      let omnisearch = "";
      
      if (keysToKeep) {
        // Only merge specified keys from existing omnisearch
        const { kvpValues } = breakApartOmnisearch(inputValue);
        for (const keyToKeep of keysToKeep) {
          const existingKvp = kvpValues.find(([k]) => k === keyToKeep);
          if (existingKvp) {
            omnisearch = addOrUpdateKeyInOmnisearch(omnisearch, existingKvp[0], existingKvp[1]);
          }
        }
      } else {
        // Default behavior: start with current omnisearch value
        omnisearch = inputValue;
      }
      
      // Add the new key-value pairs
      for (const [key, value] of keyValues) {
        omnisearch = addOrUpdateKeyInOmnisearch(omnisearch, key, value);
      }
      
      return `${window.location.protocol}//${window.location.host}${
        location.pathname
      }?${
        omnisearch.length ? `omnisearch=${encodeURIComponent(omnisearch)}` : ""
      }`;
    },
    [inputValue] // Keep inputValue dependency
  );

  const editOmnisearchKeyValues = useCallback(
    (keyVals: [string, string][]) => {
      let current = inputValue;
      for (const [key, value] of keyVals) {
        current = addOrUpdateKeyInOmnisearch(current, key, value);
      }
      setOmnisearch(current);
    },
    [inputValue, setOmnisearch]
  );

  const removeOmnisearchKeyValue = useCallback(
    (key: string) => {
      setOmnisearch(removeKeyInOmnisearch(inputValue, key));
    },
    [inputValue, setOmnisearch]
  );

  return {
    omnisearch: inputValue,
    debouncedOmnisearch: debouncedInputValue,
    getOmnisearchValue,
    createUrlFromOmnisearchKeyValues,
    setOmnisearch,
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  };
}

function addOrUpdateKeyInOmnisearch(
  search: string,
  key: string,
  value: string
) {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  
  // Remove any existing instance of the key
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  
  // Build base omnisearch with existing non-kvp values and existing kvp values (excluding the one we're updating)
  const baseOmnisearch = `${nonKvpValues} ${filteredKvp
    .map(([k, v]) => `${k}:${v}`)
    .join(" ")}`.trim();
  
  // Append the new key-value pair to the very end
  const newKeyValue = `${key}:${value}`;
  return baseOmnisearch ? `${baseOmnisearch} ${newKeyValue}` : newKeyValue;
}

function removeKeyInOmnisearch(search: string, key: string) {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  return `${nonKvpValues} ${filteredKvp
    .map(([k, v]) => `${k}:${v}`)
    .join(" ")}`.trim();
}

function breakApartOmnisearch(search: string) {
  const tokens = parseOmnisearchTokens(search);
  const nonKvpValues: string[] = [];
  const kvpValues: [string, string][] = [];
  
  for (const token of tokens) {
    const colonIndex = token.indexOf(":");
    if (colonIndex > 0) {
      // This is a key:value pair
      const key = token.substring(0, colonIndex);
      const value = token.substring(colonIndex + 1);
      kvpValues.push([key, value]);
    } else {
      // This is a non-key-value token
      nonKvpValues.push(token);
    }
  }
  
  return { 
    nonKvpValues: nonKvpValues.join(" "), 
    kvpValues 
  };
}

function parseOmnisearchTokens(search: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let inQuotes = false;
  let quoteChar = "";
  
  for (let i = 0; i < search.length; i++) {
    const char = search[i];
    
    if (!inQuotes && (char === '"' || char === "'")) {
      // Starting a quoted section
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      // Ending the quoted section
      inQuotes = false;
      quoteChar = "";
      current += char;
    } else if (!inQuotes && char === " ") {
      // Space outside quotes - end current token
      if (current.trim()) {
        tokens.push(current.trim());
        current = "";
      }
    } else {
      // Regular character
      current += char;
    }
  }
  
  // Add the last token if there is one
  if (current.trim()) {
    tokens.push(current.trim());
  }
  
  return tokens;
}
